// /////////////////////////////////////////////////////////////
// [ファイル] ProjectBuildingLinks.tsx
// [概要] SFA 案件詳細の「対象建物」セクション (絶対登録関係 ステップ3)。
//        trn_sfa_project_buildings のリンクを正本として表示・追加・解除する。
//        建物1〜4 のフリーテキストは参考表示のまま残り、本セクションが集計・監視の軸になる。
// /////////////////////////////////////////////////////////////
import React, { useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import * as Icons from '../constants/icons';
import { useAuth } from '../firebase/AuthProvider';
import { useNotification } from '../providers/NotificationProvider';
import { useConfirm } from '../providers/ConfirmProvider';
import { ep } from '../api/endpoints';

const SEARCH_BUILDINGS_URL = ep('search_crm_buildings');
const LINK_URL = ep('set_project_building_link');

interface ProjectBuildingLinksProps {
  projectId: string;
  links: any[];              // get_sales_project_detail の building_links
  onChanged: () => void;     // リンク変更後の再取得
}

export default function ProjectBuildingLinks({ projectId, links, onChanged }: ProjectBuildingLinksProps) {
  const { user } = useAuth();
  const notify = useNotification();
  const confirm = useConfirm();
  const navigate = useNavigate();

  const [options, setOptions] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchSeqRef = useRef(0);

  const handleSearch = (query: string) => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (!query.trim()) {
      setOptions([]);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    const seq = ++searchSeqRef.current;
    searchTimerRef.current = setTimeout(async () => {
      try {
        const res = await axios.post(SEARCH_BUILDINGS_URL, { query });
        if (seq !== searchSeqRef.current) return;
        setOptions(res.data.status === 'success' ? (res.data.results || []) : []);
      } catch (err) {
        if (seq !== searchSeqRef.current) return;
        console.error('[Project Building Search] Error:', err);
        setOptions([]);
      } finally {
        if (seq === searchSeqRef.current) setSearchLoading(false);
      }
    }, 300);
  };

  const callLink = async (buildingId: string, remove: boolean) => {
    setSaving(true);
    try {
      const res = await axios.post(LINK_URL, {
        projectId,
        buildingId,
        remove,
        operator_id: user?.email,
      });
      if (res.data.status === 'success') {
        notify.success(res.data.message || '更新しました');
        onChanged();
      } else {
        notify.error(res.data.message || '更新に失敗しました');
      }
    } catch (e) {
      console.error('project building link error:', e);
      notify.error('更新に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = (building: any) => {
    if (!building) return;
    if (links.some((l) => l.building_id === building.id)) {
      notify.info('この建物は既にリンク済みです');
      return;
    }
    callLink(building.id, false);
  };

  const handleRemove = async (link: any) => {
    const ok = await confirm({
      title: '建物リンクの解除',
      message: `「${link.building_name}」とのリンクを解除しますか？（建物マスタ自体は削除されません）`,
      confirmText: '解除する',
      destructive: true,
    });
    if (!ok) return;
    callLink(link.building_id, true);
  };

  return (
    <Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
        <Icons.HomeWork sx={{ fontSize: 14, color: '#2e7d32' }} />
        対象建物（建物マスタとのリンク・集計や建物ページからの案件参照に使われます）
      </Typography>

      {links.length > 0 && (
        <Stack divider={<Divider flexItem />} sx={{ border: '1px solid #eee', borderRadius: 1.5, mb: 1.5 }}>
          {links.map((l) => (
            <Box key={l.link_id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 0.75 }}>
              <Icons.HomeWork sx={{ fontSize: 16, color: '#2e7d32', flexShrink: 0 }} />
              <Box
                sx={{ flexGrow: 1, minWidth: 0, cursor: 'pointer', '&:hover': { color: '#1976d2' } }}
                onClick={() => navigate(`/crm/customers/building/${l.building_id}`)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                  <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                    {l.building_name}
                  </Typography>
                  {l.building_type && (
                    <Chip label={l.building_type} size="small" sx={{ height: 16, fontSize: '0.6rem' }} />
                  )}
                </Box>
                <Typography variant="caption" color="text.disabled" noWrap>
                  {l.address_main || ''}
                </Typography>
              </Box>
              <Button
                size="small"
                onClick={() => handleRemove(l)}
                disabled={saving}
                sx={{ textTransform: 'none', fontSize: '0.7rem', color: '#ef5350', flexShrink: 0 }}
              >
                解除
              </Button>
            </Box>
          ))}
        </Stack>
      )}

      <Autocomplete
        options={options}
        size="small"
        loading={searchLoading}
        filterOptions={(x) => x}
        noOptionsText="建物名・住所で検索してください"
        getOptionLabel={(option: any) => option.building_name || ''}
        isOptionEqualToValue={(option: any, v: any) => option.id === v.id}
        value={null}
        onChange={(_, newValue: any) => {
          if (newValue) handleAdd(newValue);
        }}
        onInputChange={(_, newInput, reason) => {
          if (reason === 'input') handleSearch(newInput);
        }}
        renderOption={(props, option: any) => (
          <li {...props} key={option.id}>
            <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Icons.HomeWork sx={{ fontSize: 15, color: '#2e7d32' }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{option.building_name}</Typography>
                {option.building_type && (
                  <Chip label={option.building_type} size="small" sx={{ height: 16, fontSize: '0.6rem' }} />
                )}
              </Box>
              <Typography variant="caption" color="text.disabled" noWrap>
                {option.address_main || ''}
              </Typography>
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField {...params} placeholder="建物名・住所で検索してリンク追加" />
        )}
      />
    </Box>
  );
}
